import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Users, Briefcase, Building2, Activity, Search,
  CheckCircle2, XCircle, Globe, Wifi, Eye, RefreshCw, ExternalLink, ToggleRight, ToggleLeft, Radar, Play, Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function Stat({ label, value, accent, sub }) {
  return (
    <div className={`vital-card-static p-4 ${accent ? "border-primary/30" : ""}`}>
      <div className="eyebrow">{label}</div>
      <div className={`text-3xl font-black mt-1 ${accent ? "text-primary" : ""}`}>{(value ?? 0).toLocaleString?.() ?? value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminConsolePage() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    if (!user || user.role !== "admin") return;
    try {
      const r = await api.get("/admin/dashboard/overview");
      setOverview(r.data);
    } catch {
      toast.error("Failed to load overview");
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  if (!user || user.role !== "admin") {
    return (
      <div className="container py-20 max-w-xl text-center" data-testid="admin-console-locked">
        <h1 className="display-2 mb-4">Admin only</h1>
      </div>
    );
  }

  return (
    <div className="container py-10">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="admin-console-back">
        <ArrowLeft className="h-4 w-4" /> Back to Admin tools
      </Link>
      <p className="eyebrow mb-1 inline-flex items-center gap-2"><Activity className="h-3 w-3" /> Operations</p>
      <h1 className="display-2 mb-8">Admin Console</h1>

      {/* Top-line stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="overview-stats">
          <Stat label="Seekers" value={overview.users.seekers} sub={`${overview.users.subscribed} on Plus`} />
          <Stat label="Recruiters" value={overview.users.recruiters} />
          <Stat label="Companies" value={overview.companies.total} accent sub={`${overview.companies.with_jobs ?? overview.companies.with_live_jobs} have live jobs`} />
          <Stat label="Active jobs" value={overview.jobs.active} accent sub={`${overview.jobs.disabled} disabled · ${overview.jobs.recent_24h} added 24h`} />
          <Stat label="Active in last 5 min" value={overview.users.active_5m} sub={`${overview.users.active_30m} in 30m · ${overview.users.active_24h} in 24h`} />
          <Stat label="GCC feeds discovered" value={overview.companies.with_feed} sub={`${overview.companies.no_feed} unverified · ${overview.companies.discovery_pending} pending`} />
          <Stat label="Disabled users" value={overview.users.disabled} />
          <Stat label="Total users" value={overview.users.total} />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-3 md:grid-cols-6 mb-6" data-testid="console-tabs">
          <TabsTrigger value="overview" data-testid="console-tab-overview"><Activity className="h-4 w-4 mr-1.5" />Overview</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="console-tab-sessions"><Wifi className="h-4 w-4 mr-1.5" />Active sessions</TabsTrigger>
          <TabsTrigger value="crawlers" data-testid="console-tab-crawlers"><Radar className="h-4 w-4 mr-1.5" />Crawlers</TabsTrigger>
          <TabsTrigger value="companies" data-testid="console-tab-companies"><Building2 className="h-4 w-4 mr-1.5" />Companies & URLs</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="console-tab-jobs"><Briefcase className="h-4 w-4 mr-1.5" />Jobs</TabsTrigger>
          <TabsTrigger value="scans" data-testid="console-tab-scans"><Sparkles className="h-4 w-4 mr-1.5" />Resume scans</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {overview && (
            <div className="grid lg:grid-cols-2 gap-6">
              <section className="vital-card-static p-5" data-testid="recent-crawls">
                <h3 className="font-bold mb-3 inline-flex items-center gap-2"><RefreshCw className="h-4 w-4" /> Latest crawler runs</h3>
                <ul className="space-y-3 text-sm">
                  {overview.recent_crawls.map((r, i) => (
                    <li key={i} className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{r.company || r.external_source}</div>
                        <div className="text-xs text-muted-foreground truncate">{r.external_source} · {new Date(r.started_at).toLocaleString()}</div>
                      </div>
                      <div className="text-right text-xs shrink-0">
                        <div className={`font-bold ${r.india_postings > 0 ? "text-emerald-600" : "text-muted-foreground"}`}>{r.india_postings} jobs</div>
                        {r.errors?.length > 0 && <div className="text-red-500 truncate max-w-[140px]">{r.errors[0]}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="vital-card-static p-5" data-testid="latest-users">
                <h3 className="font-bold mb-3 inline-flex items-center gap-2"><Users className="h-4 w-4" /> Latest signups</h3>
                <ul className="space-y-3 text-sm">
                  {overview.latest_users.map((u) => (
                    <li key={u.user_id} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{u.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      </div>
                      <div className="text-xs text-muted-foreground shrink-0"><span className="pill">{u.role}</span> · {new Date(u.created_at).toLocaleDateString()}</div>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          )}
        </TabsContent>

        <TabsContent value="sessions"><SessionsTab /></TabsContent>
        <TabsContent value="crawlers"><CrawlersTab /></TabsContent>
        <TabsContent value="companies"><CompaniesTab /></TabsContent>
        <TabsContent value="jobs"><JobsTab /></TabsContent>
        <TabsContent value="scans"><ResumeScansTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Resume Scans tab — background jobs triggered by resume uploads ───────
function ResumeScansTab() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/resume-scans", { params: statusFilter !== "all" ? { status: statusFilter } : {} });
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 5s so admins see waiting → pass transitions live.
  useEffect(() => {
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const badge = (s) => {
    const map = {
      waiting:  "bg-slate-500/15 text-slate-700 dark:text-slate-300",
      started:  "bg-blue-500/15  text-blue-700  dark:text-blue-300",
      running:  "bg-blue-500/15  text-blue-700  dark:text-blue-300 animate-pulse",
      pass:     "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
      fail:     "bg-rose-500/15 text-rose-700 dark:text-rose-300",
    };
    return <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${map[s] || map.waiting}`}>{s}</span>;
  };

  return (
    <div data-testid="scans-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["all", "waiting", "started", "running", "pass", "fail"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${statusFilter === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
            data-testid={`scans-filter-${s}`}
          >
            {s}{s !== "all" && counts[s] ? ` (${counts[s]})` : ""}
          </button>
        ))}
        <div className="ml-auto text-xs text-muted-foreground">
          {loading ? "refreshing…" : `${items.length} runs`}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border" data-testid="scans-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Slot / label</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Matches</th>
              <th className="text-right px-3 py-2">Strong</th>
              <th className="text-right px-3 py-2">Duration</th>
              <th className="text-left px-3 py-2">Created</th>
              <th className="text-left px-3 py-2">Fail reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((it) => (
              <tr key={it.id} className="hover:bg-muted/30" data-testid={`scan-row-${it.id}`}>
                <td className="px-3 py-2">
                  <div className="font-semibold">{it.user_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{it.user_email}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className="font-mono">#{it.slot + 1}</span> · {it.slot_label}
                </td>
                <td className="px-3 py-2">{badge(it.status)}</td>
                <td className="px-3 py-2 text-right font-mono">{it.matches_found ?? 0}</td>
                <td className="px-3 py-2 text-right font-mono">{it.strong_fits ?? 0}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{it.duration_ms != null ? `${it.duration_ms}ms` : "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(it.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs text-rose-600 dark:text-rose-400 max-w-[240px] truncate" title={it.fail_reason || ""}>{it.fail_reason || ""}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground text-sm">No scan runs yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SessionsTab() {
  const [items, setItems] = useState([]);
  const [windowMin, setWindowMin] = useState("30");
  useEffect(() => {
    api.get(`/admin/sessions/active?window_minutes=${windowMin}`).then((r) => setItems(r.data.items || []));
  }, [windowMin]);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{items.length} users seen in the last {windowMin} min.</p>
        <Select value={windowMin} onValueChange={setWindowMin}>
          <SelectTrigger className="w-40" data-testid="sessions-window"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[5, 30, 60, 360, 1440].map((m) => <SelectItem key={m} value={String(m)}>Last {m >= 60 ? `${Math.round(m / 60)}h` : `${m} min`}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border" data-testid="sessions-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground tracking-wider">
            <tr><th className="py-2 px-3 text-left">User</th><th className="py-2 px-3 text-left">Role</th><th className="py-2 px-3 text-left">Provider</th><th className="py-2 px-3 text-left">Last seen</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((u) => (
              <tr key={u.user_id} data-testid={`session-${u.user_id}`}>
                <td className="py-2 px-3"><div className="font-semibold">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                <td className="py-2 px-3"><span className="pill">{u.role}</span></td>
                <td className="py-2 px-3 text-xs text-muted-foreground">{u.auth_provider}</td>
                <td className="py-2 px-3 text-xs">{new Date(u.last_seen_at).toLocaleString()}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-muted-foreground" data-testid="sessions-empty">No active sessions in this window.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CrawlersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rerunning, setRerunning] = useState({});
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/crawlers");
      setItems(r.data.items || []);
    } catch {
      toast.error("Failed to load crawlers");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rerun = async (companyName) => {
    setRerunning((m) => ({ ...m, [companyName]: true }));
    try {
      await api.post("/admin/crawlers/run", { company_name: companyName });
      toast.success(`Started ${companyName} — refreshing list in 10s`);
      setTimeout(load, 10000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not start ${companyName}`);
    } finally {
      setTimeout(() => setRerunning((m) => { const n = { ...m }; delete n[companyName]; return n; }), 8000);
    }
  };

  const filtered = items.filter((it) => {
    if (kindFilter !== "all" && it.kind !== kindFilter) return false;
    if (q) {
      const needle = q.toLowerCase();
      return (it.company_name || "").toLowerCase().includes(needle)
        || (it.external_source || "").toLowerCase().includes(needle)
        || (it.api_url || "").toLowerCase().includes(needle);
    }
    return true;
  });

  const statusPill = (s) => {
    if (s === "success") return <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Success</span>;
    if (s === "failed") return <span className="inline-flex items-center gap-1 text-red-500 text-xs font-semibold"><XCircle className="h-3.5 w-3.5" /> Failed</span>;
    return <span className="inline-flex items-center gap-1 text-muted-foreground text-xs"><Activity className="h-3.5 w-3.5" /> Never run</span>;
  };

  const totalActive = filtered.reduce((a, c) => a + (c.active_jobs || 0), 0);
  const totalInactive = filtered.reduce((a, c) => a + (c.inactive_jobs || 0), 0);

  return (
    <div data-testid="crawlers-tab">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="flex items-center gap-2 px-3 border border-border rounded-lg bg-background flex-1 max-w-md">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search company / source / URL…"
            className="border-0 focus-visible:ring-0 shadow-none"
            data-testid="crawlers-search"
          />
        </div>
        <Select value={kindFilter} onValueChange={setKindFilter}>
          <SelectTrigger className="w-40" data-testid="crawlers-kind"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All crawlers</SelectItem>
            <SelectItem value="bespoke">Bespoke only</SelectItem>
            <SelectItem value="generic">Generic only</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="crawlers-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        {filtered.length} sources · <span className="font-semibold text-foreground">{totalActive.toLocaleString()}</span> active jobs · {totalInactive.toLocaleString()} disabled
      </p>
      <div className="overflow-x-auto rounded-lg border border-border" data-testid="crawlers-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground tracking-wider">
            <tr>
              <th className="py-2 px-3 text-left">Company</th>
              <th className="py-2 px-3 text-left">ATS</th>
              <th className="py-2 px-3 text-left">URL</th>
              <th className="py-2 px-3 text-left">Last run</th>
              <th className="py-2 px-3 text-left">Status</th>
              <th className="py-2 px-3 text-right">Active</th>
              <th className="py-2 px-3 text-right">Disabled</th>
              <th className="py-2 px-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((it) => {
              const lr = it.last_run;
              const errs = lr?.errors || [];
              return (
                <tr key={`${it.kind}-${it.external_source}`} data-testid={`crawler-row-${it.external_source}`}>
                  <td className="py-2 px-3">
                    <div className="font-semibold">{it.company_name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                      {it.kind === "bespoke" ? <span className="pill bg-primary/10 text-primary border-primary/20">Bespoke</span> : <span className="pill">Generic</span>}
                      <span className="font-mono truncate max-w-[200px]">{it.external_source}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-xs">{it.ats}</td>
                  <td className="py-2 px-3 max-w-md">
                    <a
                      href={it.api_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-mono text-primary hover:underline truncate inline-flex items-center gap-1 max-w-full"
                      title={it.api_url}
                      data-testid={`crawler-url-${it.external_source}`}
                    >
                      <span className="truncate">{it.api_url}</span>
                      <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
                    </a>
                  </td>
                  <td className="py-2 px-3 text-xs">
                    {lr?.started_at ? new Date(lr.started_at).toLocaleString() : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 px-3">
                    {statusPill(it.status)}
                    {errs.length > 0 && <div className="text-[10px] text-red-500 truncate max-w-[160px] mt-0.5" title={errs.join("\n")}>{errs[0]}</div>}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">
                    <span className={it.active_jobs > 0 ? "text-emerald-600 font-bold" : "text-muted-foreground"}>{(it.active_jobs || 0).toLocaleString()}</span>
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-muted-foreground">{(it.inactive_jobs || 0).toLocaleString()}</td>
                  <td className="py-2 px-3 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => rerun(it.company_name)}
                      disabled={!!rerunning[it.company_name]}
                      data-testid={`crawler-rerun-${it.external_source}`}
                    >
                      {rerunning[it.company_name] ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <><Play className="h-3.5 w-3.5 mr-1" /> Rerun</>}
                    </Button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={8} className="py-12 text-center text-muted-foreground" data-testid="crawlers-empty">No crawlers match this filter.</td></tr>
            )}
            {loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="py-12 text-center text-muted-foreground">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompaniesTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [discovery, setDiscovery] = useState("all");
  const [hasJobs, setHasJobs] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 200;

  useEffect(() => { setPage(0); }, [discovery, hasJobs, q]);

  useEffect(() => {
    const params = { discovery, limit: PAGE_SIZE, skip: page * PAGE_SIZE };
    if (q) params.q = q;
    if (hasJobs !== "all") params.has_jobs = hasJobs === "true";
    api.get("/admin/companies/coverage-table", { params }).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  }, [discovery, hasJobs, q, page]);

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div>
      <div className="vital-card-static p-3 mb-4 grid md:grid-cols-[1fr_180px_180px] gap-2" data-testid="companies-filters">
        <div className="flex items-center gap-2 px-3 border border-border rounded-lg bg-background">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name…" className="border-0 focus-visible:ring-0 shadow-none" data-testid="companies-search" />
        </div>
        <Select value={discovery} onValueChange={setDiscovery}>
          <SelectTrigger data-testid="companies-discovery"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All URL states</SelectItem>
            <SelectItem value="found">Feed discovered</SelectItem>
            <SelectItem value="not_found">No feed yet</SelectItem>
            <SelectItem value="pending">Pending discovery</SelectItem>
          </SelectContent>
        </Select>
        <Select value={hasJobs} onValueChange={setHasJobs}>
          <SelectTrigger data-testid="companies-hasjobs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Jobs: any</SelectItem>
            <SelectItem value="true">Has live jobs</SelectItem>
            <SelectItem value="false">No live jobs</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-muted-foreground" data-testid="companies-range">
          Showing <span className="font-bold">{rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()}</span> of <span className="font-bold">{total.toLocaleString()}</span>
        </p>
        {total > PAGE_SIZE && (
          <div className="flex items-center gap-2" data-testid="companies-pager">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(0)}>« First</Button>
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>‹ Prev</Button>
            <span className="text-xs text-muted-foreground px-2">Page {page + 1} of {lastPage + 1}</span>
            <Button size="sm" variant="outline" disabled={page >= lastPage} onClick={() => setPage(p => p + 1)}>Next ›</Button>
            <Button size="sm" variant="outline" disabled={page >= lastPage} onClick={() => setPage(lastPage)}>Last »</Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto rounded-lg border border-border" data-testid="companies-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground tracking-wider">
            <tr>
              <th className="py-2 px-3 text-left">Company</th>
              <th className="py-2 px-3 text-left">Sector</th>
              <th className="py-2 px-3 text-left">ATS</th>
              <th className="py-2 px-3 text-left">Careers URL (sourced/proposed)</th>
              <th className="py-2 px-3 text-right">Live jobs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((c) => (
              <tr key={c.company_id} data-testid={`company-row-${c.company_id}`}>
                <td className="py-2 px-3 font-semibold">{c.name}</td>
                <td className="py-2 px-3 text-muted-foreground">{c.sector}</td>
                <td className="py-2 px-3">
                  {c.ats
                    ? <span className="pill"><CheckCircle2 className="h-3 w-3 mr-1 inline text-emerald-500" />{c.ats}</span>
                    : <span className="pill text-muted-foreground"><XCircle className="h-3 w-3 mr-1 inline" />none</span>}
                </td>
                <td className="py-2 px-3 text-xs">
                  {c.careers_url
                    ? <a href={c.careers_url} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate inline-block max-w-md">{c.careers_url} <ExternalLink className="inline h-3 w-3" /></a>
                    : <span className="text-muted-foreground italic">not yet discovered</span>}
                </td>
                <td className="py-2 px-3 text-right font-bold">{c.live_jobs}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-muted-foreground" data-testid="companies-empty">No matches.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JobsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("active");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  // Reset to page 0 whenever the filter changes
  useEffect(() => { setPage(0); }, [status, q, pageSize]);

  const load = useCallback(async () => {
    const params = { status, limit: pageSize, skip: page * pageSize };
    if (q) params.q = q;
    const r = await api.get("/admin/jobs/list", { params });
    setItems(r.data.items || []);
    setTotal(r.data.total || 0);
  }, [status, q, page, pageSize]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (jobId) => {
    setBusy(jobId);
    try {
      await api.patch(`/admin/jobs/${jobId}/toggle`);
      toast.success("Toggled");
      load();
    } catch {
      toast.error("Toggle failed");
    } finally { setBusy(null); }
  };

  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  const rangeStart = total === 0 ? 0 : page * pageSize + 1;
  const rangeEnd = Math.min(total, (page + 1) * pageSize);

  return (
    <div>
      <div className="vital-card-static p-3 mb-4 grid md:grid-cols-[1fr_180px_140px] gap-2" data-testid="jobs-filters">
        <div className="flex items-center gap-2 px-3 border border-border rounded-lg bg-background">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by title, company, location…" className="border-0 focus-visible:ring-0 shadow-none" data-testid="jobs-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="jobs-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All jobs</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
          <SelectTrigger data-testid="jobs-pagesize"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[50, 100, 200, 500].map((n) => <SelectItem key={n} value={String(n)}>{n} per page</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-muted-foreground" data-testid="jobs-range">
          Showing <span className="font-bold">{rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()}</span> of <span className="font-bold">{total.toLocaleString()}</span>
        </p>
        <div className="flex items-center gap-2" data-testid="jobs-pager">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(0)} data-testid="jobs-page-first">« First</Button>
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="jobs-page-prev">‹ Prev</Button>
          <span className="text-xs text-muted-foreground px-2">Page {page + 1} of {lastPage + 1}</span>
          <Button size="sm" variant="outline" disabled={page >= lastPage} onClick={() => setPage(p => p + 1)} data-testid="jobs-page-next">Next ›</Button>
          <Button size="sm" variant="outline" disabled={page >= lastPage} onClick={() => setPage(lastPage)} data-testid="jobs-page-last">Last »</Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border" data-testid="jobs-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground tracking-wider">
            <tr>
              <th className="py-2 px-3 text-left">Title</th>
              <th className="py-2 px-3 text-left">Company</th>
              <th className="py-2 px-3 text-left">Location</th>
              <th className="py-2 px-3 text-left">Status</th>
              <th className="py-2 px-3 text-left">Source</th>
              <th className="py-2 px-3 text-right">Toggle</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((j) => (
              <tr key={j.job_id} data-testid={`job-row-${j.job_id}`}>
                <td className="py-2 px-3 font-semibold truncate max-w-xs">{j.title}</td>
                <td className="py-2 px-3 text-muted-foreground">{j.company}</td>
                <td className="py-2 px-3 text-xs text-muted-foreground">{j.location}</td>
                <td className="py-2 px-3">
                  {j.is_active
                    ? <span className="text-[11px] px-2 py-0.5 rounded font-semibold bg-emerald-500/10 text-emerald-700">active</span>
                    : <span className="text-[11px] px-2 py-0.5 rounded font-semibold bg-red-500/10 text-red-700">disabled</span>}
                </td>
                <td className="py-2 px-3 text-xs text-muted-foreground truncate max-w-[150px]">{j.external_source || "—"}</td>
                <td className="py-2 px-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => toggle(j.job_id)} disabled={busy === j.job_id} data-testid={`toggle-job-${j.job_id}`}>
                    {j.is_active ? <><ToggleLeft className="mr-1 h-4 w-4" />Disable</> : <><ToggleRight className="mr-1 h-4 w-4" />Enable</>}
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground" data-testid="jobs-empty">No jobs match.</td></tr>}
          </tbody>
        </table>
      </div>

      {total > pageSize && (
        <div className="flex items-center justify-end gap-2 mt-3" data-testid="jobs-pager-bottom">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(0)}>« First</Button>
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>‹ Prev</Button>
          <span className="text-xs text-muted-foreground px-2">Page {page + 1} of {lastPage + 1}</span>
          <Button size="sm" variant="outline" disabled={page >= lastPage} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button size="sm" variant="outline" disabled={page >= lastPage} onClick={() => setPage(lastPage)}>Last »</Button>
        </div>
      )}
    </div>
  );
}
